import React, { useState, useRef, useEffect } from 'react';
import { sendAIMessage, ChatMessage } from 'interfaces/ai';

const SUGGESTED_CATEGORIES = [
    {
        label: '📊 CA & commandes',
        questions: [
            'Quel est mon CA ce mois-ci ?',
            'Comment évolue mon CA par rapport au mois dernier ?',
            'Quel est mon panier moyen ce mois-ci ?',
            'Combien de commandes sont en attente de livraison ?',
            'Y a-t-il des commandes avec des problèmes à résoudre ?',
        ],
    },
    {
        label: '📦 Stock & production',
        questions: [
            'Quels produits sont en alerte de stock ?',
            'Quels ingrédients sont en alerte de stock ?',
            'Combien de productions sont planifiées ?',
            'Y a-t-il des productions en attente de validation ?',
            'Combien de bons de commande sont en attente de réception ?',
        ],
    },
    {
        label: '💰 Trésorerie',
        questions: [
            "Combien d'impayés ai-je en cours ?",
            'Y a-t-il des factures en retard de paiement ?',
            'Quel est le montant total de mes factures en retard ?',
        ],
    },
    {
        label: '👥 Clients',
        questions: [
            'Quels clients sont inactifs depuis longtemps ?',
            "Combien de clients n'ont jamais passé de commande ?",
            'Quels sont mes 5 meilleurs produits ce mois-ci ?',
        ],
    },
    {
        label: '🎯 Synthèse',
        questions: [
            'Fais-moi un résumé complet de mon activité',
            'Quelles sont mes priorités du moment ?',
            'Quels sont les points de vigilance actuels ?',
        ],
    },
];

const WELCOME_MESSAGE: ChatMessage = {
    role: 'assistant',
    content:
        'Bonjour 👋 Je suis **ToolAssist**, votre assistant de pilotage. Je peux analyser vos données en temps réel (stock, commandes, CA, clients…) et vous aider à prendre de meilleures décisions. Que souhaitez-vous savoir ?',
};

const AIAssistant: React.FC = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [messages, setMessages] = useState<ChatMessage[]>([]);
    const [input, setInput] = useState('');
    const [loading, setLoading] = useState(false);
    const bottomRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const inFlightRef = useRef(false);

    useEffect(() => {
        if (isOpen && messages.length === 0) {
            setMessages([WELCOME_MESSAGE]);
        }
        if (isOpen) {
            setTimeout(() => inputRef.current?.focus(), 100);
        }
    }, [isOpen, messages.length]);

    useEffect(() => {
        bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages, loading]);

    const handleSend = async (text?: string) => {
        const content = (text ?? input).trim();
        if (!content || inFlightRef.current) return;
        inFlightRef.current = true;

        let snapshot: ChatMessage[] = [];
        setMessages((prev) => {
            snapshot = [...prev, { role: 'user', content }];
            return snapshot;
        });
        setInput('');
        setLoading(true);

        await Promise.resolve();
        const result = await sendAIMessage(snapshot);

        setMessages((prev) => [
            ...prev,
            result?.reply
                ? { role: 'assistant', content: result.reply }
                : { role: 'assistant', content: 'Une erreur est survenue. Veuillez réessayer.' },
        ]);
        setLoading(false);
        inFlightRef.current = false;
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    };

    /** Very lightweight markdown-like renderer for bold (**text**) and newlines */
    const renderContent = (content: string) => {
        const parts = content.split(/(\*\*[^*]+\*\*)/g);
        return parts.map((part, i) => {
            if (part.startsWith('**') && part.endsWith('**')) {
                return <strong key={i}>{part.slice(2, -2)}</strong>;
            }
            return (
                <span key={i}>
                    {part.split('\n').map((line, j, arr) => (
                        <React.Fragment key={j}>
                            {line}
                            {j < arr.length - 1 && <br />}
                        </React.Fragment>
                    ))}
                </span>
            );
        });
    };

    return (
        <>
            {/* Floating button */}
            <button
                onClick={() => setIsOpen((o) => !o)}
                className={`fixed bottom-[64px] right-6 z-[9999] flex h-14 w-14 items-center justify-center rounded-full shadow-xl transition-all duration-200 ${
                    isOpen
                        ? 'bg-gray-700 text-white'
                        : 'bg-toola-500 text-white hover:bg-toola-600'
                }`}
                title="Assistant IA Toola"
                aria-label="Ouvrir l'assistant IA"
            >
                {isOpen ? (
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-6 w-6"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={2}
                    >
                        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                ) : (
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-7 w-7"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={1.8}
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09z"
                        />
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456z"
                        />
                    </svg>
                )}
            </button>

            {/* Chat panel */}
            {isOpen && (
                <div className="fixed bottom-[134px] right-6 z-[9998] flex w-[360px] flex-col rounded-2xl bg-white shadow-2xl dark:bg-navy-800 border border-gray-200 dark:border-navy-700 overflow-hidden"
                    style={{ maxHeight: 'calc(100vh - 130px)' }}
                >
                    {/* Header */}
                    <div className="flex items-center gap-3 bg-toola-500 px-4 py-3">
                        <div className="flex h-8 w-8 items-center justify-center rounded-full bg-white/20">
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="h-5 w-5 text-white"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                                strokeWidth={1.8}
                            >
                                <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09z" />
                            </svg>
                        </div>
                        <div>
                            <p className="text-sm font-semibold text-white leading-tight">ToolAssist</p>
                            <p className="text-xs text-white/70">Pilotage & aide à la décision</p>
                        </div>
                        <button
                            onClick={() => setMessages([])}
                            className="ml-auto text-xs text-white/60 hover:text-white transition-colors"
                            title="Nouvelle conversation"
                        >
                            Réinitialiser
                        </button>
                    </div>

                    {/* Messages */}
                    <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3" style={{ minHeight: 200, maxHeight: 380 }}>
                        {messages.map((msg, idx) => (
                            <div
                                key={idx}
                                className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                            >
                                <div
                                    className={`max-w-[85%] rounded-2xl px-3 py-2 text-sm leading-relaxed ${
                                        msg.role === 'user'
                                            ? 'bg-toola-500 text-white rounded-br-sm'
                                            : 'bg-gray-100 dark:bg-navy-700 text-gray-800 dark:text-white rounded-bl-sm'
                                    }`}
                                >
                                    {renderContent(msg.content)}
                                </div>
                            </div>
                        ))}

                        {loading && (
                            <div className="flex justify-start">
                                <div className="rounded-2xl rounded-bl-sm bg-gray-100 dark:bg-navy-700 px-4 py-3">
                                    <div className="flex gap-1">
                                        <span className="h-2 w-2 rounded-full bg-toola-400 animate-bounce" style={{ animationDelay: '0ms' }} />
                                        <span className="h-2 w-2 rounded-full bg-toola-400 animate-bounce" style={{ animationDelay: '150ms' }} />
                                        <span className="h-2 w-2 rounded-full bg-toola-400 animate-bounce" style={{ animationDelay: '300ms' }} />
                                    </div>
                                </div>
                            </div>
                        )}

                        <div ref={bottomRef} />
                    </div>

                    {/* Suggested questions (only before first user message) */}
                    {messages.filter((m) => m.role === 'user').length === 0 && (
                        <div className="px-3 pb-2 space-y-2">
                            {SUGGESTED_CATEGORIES.map((cat) => (
                                <div key={cat.label}>
                                    <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400 dark:text-navy-400 mb-1">
                                        {cat.label}
                                    </p>
                                    <div className="flex gap-1.5 overflow-x-auto pb-1 scrollbar-hide">
                                        {cat.questions.map((q) => (
                                            <button
                                                key={q}
                                                onClick={() => handleSend(q)}
                                                disabled={loading}
                                                className="shrink-0 text-xs rounded-full border border-toola-400 text-toola-500 px-2.5 py-1 hover:bg-toola-500 hover:text-white transition-colors whitespace-nowrap disabled:opacity-40 disabled:pointer-events-none"
                                            >
                                                {q}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Input */}
                    <div className="border-t border-gray-200 dark:border-navy-600 px-3 py-2 flex gap-2 items-end">
                        <input
                            ref={inputRef}
                            type="text"
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            onKeyDown={handleKeyDown}
                            placeholder="Posez votre question…"
                            disabled={loading}
                            className="flex-1 rounded-xl border border-gray-200 dark:border-navy-600 bg-gray-50 dark:bg-navy-700 px-3 py-2 text-sm text-gray-800 dark:text-white outline-none focus:ring-2 focus:ring-toola-400 disabled:opacity-50 resize-none"
                        />
                        <button
                            onClick={() => handleSend()}
                            disabled={!input.trim() || loading}
                            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-toola-500 text-white hover:bg-toola-600 disabled:opacity-40 transition-colors"
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="h-4 w-4"
                                viewBox="0 0 24 24"
                                fill="currentColor"
                            >
                                <path d="M3.478 2.405a.75.75 0 00-.926.94l2.432 7.905H13.5a.75.75 0 010 1.5H4.984l-2.432 7.905a.75.75 0 00.926.94 60.519 60.519 0 0018.445-8.986.75.75 0 000-1.218A60.517 60.517 0 003.478 2.405z" />
                            </svg>
                        </button>
                    </div>
                </div>
            )}
        </>
    );
};

export default AIAssistant;
